'use client';

// Inline impersonation banner — rendered alongside the demo/live network
// banner inside the sticky app header. Engineer-only path; renders
// nothing for non-impersonating sessions.
import { c as _c } from "react/compiler-runtime";
import { useImpersonation } from '@morfi/backend-sdk-v2/client';
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { UserIdentity } from '@/components/profile/user-identity';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Link, usePathname } from '@/i18n/navigation';
import { stripDemoModePath, stripLeadingLocalePath } from '@/lib/network';
import { logger } from '@/lib/telemetry/logger';
import { cn } from '@/lib/ui/utils';
export function ImpersonationBanner() {
  const $ = _c(91);
  const {
    isActive,
    target,
    expiresAt,
    stop
  } = useImpersonation();
  const t = useTranslations("impersonation.banner");
  const pathname = usePathname();
  const prefersReduced = useReducedMotion();
  const [now, setNow] = useState(_temp);
  let t0;
  if ($[0] !== pathname || $[1] !== target) {
    t0 = target !== null && isImpersonatedPortfolioPath(pathname, target.userId);
    $[0] = pathname;
    $[1] = target;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const restoreOwnPortfolio = t0;
  let t1;
  if ($[3] !== restoreOwnPortfolio || $[4] !== stop) {
    t1 = function handleStop() {
      if (restoreOwnPortfolio) {
        logger.info("impersonation.stop.target_portfolio_redirect");
      }
      stop();
    };
    $[3] = restoreOwnPortfolio;
    $[4] = stop;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleStop = t1;
  let t2;
  let t3;
  if ($[6] !== isActive) {
    t2 = () => {
      if (!isActive) {
        return;
      }
      const id = window.setInterval(() => {
        setNow(Date.now());
      }, 1000);
      return () => {
        window.clearInterval(id);
      };
    };
    t3 = [isActive];
    $[6] = isActive;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  if (!isActive) {
    return null;
  }
  const expiresAtMs = typeof expiresAt === "number" ? expiresAt : null;
  const remainingMs = expiresAtMs !== null ? Math.max(0, expiresAtMs - now) : null;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== prefersReduced || $[10] !== remainingMs || $[11] !== t || $[12] !== target) {
    const countdown = formatCountdown(remainingMs ?? 0);
    const expired = remainingMs !== null && remainingMs === 0;
    const displayName = target?.displayName ?? null;
    const handle = target?.handle ?? null;
    let t18;
    if ($[29] !== target) {
      t18 = target?.primaryWallet !== null && target?.primaryWallet !== undefined ? shortWallet(target.primaryWallet) : "\u2014";
      $[29] = target;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    const fallbackName = t18;
    const initialY = prefersReduced ? 0 : -32;
    T1 = LazyMotion;
    t16 = domAnimation;
    t17 = true;
    T0 = m.div;
    t9 = "alert";
    t10 = "polite";
    const t19 = prefersReduced ? 1 : 0;
    if ($[31] !== initialY || $[32] !== t19) {
      t11 = {
        y: initialY,
        opacity: t19
      };
      $[31] = initialY;
      $[32] = t19;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        y: 0,
        opacity: 1
      };
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    if ($[35] !== prefersReduced) {
      t13 = prefersReduced ? {
        duration: 0
      } : {
        duration: 0.2,
        ease: [0.4, 0, 0.2, 1]
      };
      $[35] = prefersReduced;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = cn("relative z-sticky w-full border-b border-border-primary bg-feedback-warning-accent/15", "px-3 py-1.5 sm:px-4 sm:py-2");
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    t15 = "impersonation-banner";
    t7 = "mx-auto flex max-w-screen-2xl items-center justify-between gap-2 sm:gap-3";
    let t20;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <span className="shrink-0 rounded-item bg-feedback-warning-accent/30 px-1.5 py-0.5 text-micro font-bold uppercase tracking-wide text-feedback-warning-content">DEV</span>;
      $[38] = t20;
    } else {
      t20 = $[38];
    }
    const t21 = target?.userId ?? null;
    const t22 = target?.avatarUrl ?? null;
    const t23 = target?.avatarNsfw ?? false;
    const t24 = target?.avatarPreferences?.rasterise ?? false;
    let t25;
    if ($[39] !== displayName || $[40] !== t21 || $[41] !== t22 || $[42] !== t23 || $[43] !== t24) {
      t25 = <UserAvatar userId={t21} displayName={displayName} avatarUrl={t22} nsfw={t23} rasterise={t24} size="xs" />;
      $[39] = displayName;
      $[40] = t21;
      $[41] = t22;
      $[42] = t23;
      $[43] = t24;
      $[44] = t25;
    } else {
      t25 = $[44];
    }
    let t26;
    if ($[45] !== displayName || $[46] !== fallbackName || $[47] !== handle || $[48] !== target?.avatarNsfw || $[49] !== target?.avatarUrl || $[50] !== target?.connectedProviders || $[51] !== target?.userId) {
      t26 = <span className="min-w-0 truncate text-caption text-foreground-primary">{displayName !== null || handle !== null ? <UserIdentity user={{
          userId: target?.userId ?? null,
          displayName,
          handle,
          avatarUrl: target?.avatarUrl ?? null,
          avatarNsfw: target?.avatarNsfw ?? false,
          connectedProviders: target?.connectedProviders ?? []
        }} size="sm" /> : <span className="font-mono">{fallbackName}</span>}</span>;
      $[45] = displayName;
      $[46] = fallbackName;
      $[47] = handle;
      $[48] = target?.avatarNsfw;
      $[49] = target?.avatarUrl;
      $[50] = target?.connectedProviders;
      $[51] = target?.userId;
      $[52] = t26;
    } else {
      t26 = $[52];
    }
    let t27;
    if ($[53] !== t) {
      t27 = t("title", {
        name: ""
      }).replace("  \u2014", "\u2014");
      $[53] = t;
      $[54] = t27;
    } else {
      t27 = $[54];
    }
    let t28;
    if ($[55] !== t27) {
      t28 = <span className="hidden shrink-0 text-caption text-foreground-secondary md:inline">— {t27}</span>;
      $[55] = t27;
      $[56] = t28;
    } else {
      t28 = $[56];
    }
    if ($[57] !== t25 || $[58] !== t26 || $[59] !== t28) {
      t8 = <div className="flex min-w-0 flex-1 items-center gap-2">{t20}{t25}{t26}{t28}</div>;
      $[57] = t25;
      $[58] = t26;
      $[59] = t28;
      $[60] = t8;
    } else {
      t8 = $[60];
    }
    t6 = "flex shrink-0 items-center gap-2 sm:gap-3";
    t4 = "hidden font-mono text-caption tabular-nums text-foreground-secondary sm:inline";
    t5 = expired ? t("expired") : t("expires_in", {
      countdown
    });
    $[9] = prefersReduced;
    $[10] = remainingMs;
    $[11] = t;
    $[12] = target;
    $[13] = T0;
    $[14] = T1;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
    $[22] = t17;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    T0 = $[13];
    T1 = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t15 = $[20];
    t16 = $[21];
    t17 = $[22];
    t4 = $[23];
    t5 = $[24];
    t6 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  let t18;
  if ($[61] !== t4 || $[62] !== t5) {
    t18 = <span className={t4}>{t5}</span>;
    $[61] = t4;
    $[62] = t5;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  let t19;
  if ($[64] !== handleStop || $[65] !== restoreOwnPortfolio || $[66] !== t) {
    t19 = restoreOwnPortfolio ? <AnimatedButton asChild={true} variant="secondary" size="sm"><Link href="/portfolio" onClick={handleStop} data-testid="impersonation-banner-stop">{t("stop")}</Link></AnimatedButton> : <AnimatedButton variant="secondary" size="sm" onClick={handleStop} data-testid="impersonation-banner-stop">{t("stop")}</AnimatedButton>;
    $[64] = handleStop;
    $[65] = restoreOwnPortfolio;
    $[66] = t;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== t18 || $[69] !== t19 || $[70] !== t6) {
    t20 = <div className={t6}>{t18}{t19}</div>;
    $[68] = t18;
    $[69] = t19;
    $[70] = t6;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  let t21;
  if ($[72] !== t20 || $[73] !== t7 || $[74] !== t8) {
    t21 = <div className={t7}>{t8}{t20}</div>;
    $[72] = t20;
    $[73] = t7;
    $[74] = t8;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  let t22;
  if ($[76] !== T0 || $[77] !== t10 || $[78] !== t11 || $[79] !== t12 || $[80] !== t13 || $[81] !== t14 || $[82] !== t15 || $[83] !== t21 || $[84] !== t9) {
    t22 = <T0 role={t9} aria-live={t10} initial={t11} animate={t12} transition={t13} className={t14} data-testid={t15}>{t21}</T0>;
    $[76] = T0;
    $[77] = t10;
    $[78] = t11;
    $[79] = t12;
    $[80] = t13;
    $[81] = t14;
    $[82] = t15;
    $[83] = t21;
    $[84] = t9;
    $[85] = t22;
  } else {
    t22 = $[85];
  }
  let t23;
  if ($[86] !== T1 || $[87] !== t16 || $[88] !== t17 || $[89] !== t22) {
    t23 = <T1 features={t16} strict={t17}>{t22}</T1>;
    $[86] = T1;
    $[87] = t16;
    $[88] = t17;
    $[89] = t22;
    $[90] = t23;
  } else {
    t23 = $[90];
  }
  return t23;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function _temp() {
  return Date.now();
}
function formatCountdown(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}
function shortWallet(addr: string): string {
  if (addr.length <= 10) return addr;
  return `${addr.slice(0, 4)}…${addr.slice(-4)}`;
}
function isImpersonatedPortfolioPath(pathname: string, targetUserId: string): boolean {
  const barePath = stripDemoModePath(stripLeadingLocalePath(pathname));
  return barePath === `/portfolio/${targetUserId}` || barePath === `/portfolio/${encodeURIComponent(targetUserId)}`;
}